import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listLiveProducts,
  getShopSettings,
  updateShopSettings,
  type ShopSettings,
  type HeroElement,
} from "@/lib/products";
import type { RichProduct } from "@/lib/products";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ShoppingBag,
  Pencil,
  Star,
  Sparkles,
  Eye,
  EyeOff,
  GripVertical,
  GripVertical as DragHandle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { FlightDeckAI } from "@/components/FlightDeckAI";
import { ShopEditor } from "@/components/ShopEditor";
import { ShopBuilder } from "@/components/shop-builder/ShopBuilder";
import { InlineShopBuilder } from "@/components/shop-builder/InlineShopBuilder";
import { WidgetRenderer } from "@/components/shop-builder/WidgetRenderer";
import { toast } from "sonner";

export const Route = createFileRoute("/app/customer/shop/")({ component: CustomerShop });

function CustomerShop() {
  const { profile } = useAuth();
  const isEditor = profile?.role === "staff" || profile?.role === "admin";
  const [editorOpen, setEditorOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [inlineBuilder, setInlineBuilder] = useState(false);
  const [liveEdit, setLiveEdit] = useState(false);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [showAI, setShowAI] = useState(false);
  const qc = useQueryClient();
  const { data: settings } = useQuery<ShopSettings>({
    queryKey: ["shop-settings"],
    queryFn: getShopSettings,
  });
  const { data: liveProducts = [], isLoading } = useQuery({
    queryKey: ["live-products"],
    queryFn: listLiveProducts,
  });
  const displayTitle = settings?.hero_title ?? "Your Trade Print Shop";
  const displaySubtitle = settings?.hero_subtitle ?? "Premium custom print products for your brand";
  const displayCta = settings?.hero_cta_text ?? "Browse products";
  const bgImage = settings?.hero_image_url ?? null;
  const ctaEnabled = settings?.hero_cta_enabled ?? true;
  const aiEnabled = settings?.flightdeck_ai_enabled ?? true;
  const cardStyle = (settings?.product_card_style ?? "standard") as
    | "compact"
    | "standard"
    | "large";
  const featuredLayout = (settings?.featured_layout_mode ?? "grid") as
    | "grid"
    | "bento"
    | "carousel";
  const ribbonColor = settings?.ribbon_color ?? null;
  const ribbonGradFrom = settings?.ribbon_gradient_from ?? null;
  const ribbonGradTo = settings?.ribbon_gradient_to ?? null;
  const fullWidth = settings?.hero_full_width ?? false;
  const stickyHeader = settings?.hero_sticky_header ?? false;
  const heroHeight = settings?.hero_height_px ?? 0;
  const heroBorderRadius = settings?.hero_border_radius ?? 40;
  const gapHeroFeatured = settings?.gap_hero_featured_px ?? 32;
  const gapFeaturedProducts = settings?.gap_featured_products_px ?? 32;
  const heroElements: HeroElement[] = (settings?.hero_elements as HeroElement[]) ?? [
    { id: "title", visible: true, order: 0 },
    { id: "subtitle", visible: true, order: 1 },
    { id: "cta", visible: true, order: 2 },
    { id: "ai", visible: true, order: 3 },
  ];
  const featuredProducts = liveProducts.filter((p) =>
    (settings?.featured_product_ids ?? []).includes(p.id),
  );
  const [hideFeatured, setHideFeatured] = useState(false);
  const [productOrder, setProductOrder] = useState<string[]>([]);
  useEffect(() => {
    setProductOrder(liveProducts.map((p) => p.id));
  }, [liveProducts]);

  const reorderProducts = (fromId: string, toId: string) => {
    setProductOrder((prev) => {
      const arr = [...prev];
      const fi = arr.indexOf(fromId);
      const ti = arr.indexOf(toId);
      if (fi < 0 || ti < 0 || fi === ti) return prev;
      arr.splice(fi, 1);
      arr.splice(ti, 0, fromId);
      return arr;
    });
  };

  const sortedLiveProducts = productOrder.length
    ? [...liveProducts].sort((a, b) => productOrder.indexOf(a.id) - productOrder.indexOf(b.id))
    : liveProducts;

  const heroRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Scroll-driven hero shrink — track scroll within the main scroll container (window)
  const { scrollY } = useScroll();
  // Hero shrinks from full padding to compact as user scrolls first 180px
  const heroScrollProgress = useTransform(scrollY, [0, 180], [0, 1]);
  const heroPaddingY = useTransform(
    heroScrollProgress,
    [0, 1],
    [stickyHeader ? 56 : 0, stickyHeader ? 12 : 0],
  );
  const heroFontScale = useTransform(heroScrollProgress, [0, 1], [1, stickyHeader ? 0.72 : 1]);
  const heroOpacity = useTransform(
    heroScrollProgress,
    [0, 0.6, 1],
    [1, stickyHeader ? 0.95 : 1, stickyHeader ? 0.0 : 1],
  );

  useEffect(() => {
    if (!stickyHeader) {
      setShowStickyBar(false);
      return;
    }
    const el = heroRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => setShowStickyBar(!entry.isIntersecting), {
      rootMargin: "-40px 0px 0px 0px",
      threshold: 0,
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [stickyHeader]);

  const saveSettings = async (patch: Partial<ShopSettings>) => {
    try {
      await updateShopSettings(patch);
      qc.invalidateQueries({ queryKey: ["shop-settings"] });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const updateElement = (id: HeroElement["id"], patch: Partial<HeroElement>) => {
    const next = heroElements.map((e) => (e.id === id ? { ...e, ...patch } : e));
    saveSettings({ hero_elements: next });
  };

  const reorderElements = (fromId: string, toId: string) => {
    const sorted = [...heroElements].sort((a, b) => a.order - b.order);
    const fromIdx = sorted.findIndex((e) => e.id === fromId);
    const toIdx = sorted.findIndex((e) => e.id === toId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const [moved] = sorted.splice(fromIdx, 1);
    sorted.splice(toIdx, 0, moved);
    saveSettings({ hero_elements: sorted.map((e, i) => ({ ...e, order: i })) });
  };

  const resizingRef = useRef<{ startY: number; startH: number } | null>(null);
  const onResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = { startY: e.clientY, startH: heroRef.current?.offsetHeight ?? 0 };
    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current || !heroRef.current) return;
      const delta = ev.clientY - resizingRef.current.startY;
      const next = Math.max(180, Math.min(900, resizingRef.current.startH + delta));
      heroRef.current.style.minHeight = `${next}px`;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (heroRef.current) saveSettings({ hero_height_px: heroRef.current.offsetHeight });
      resizingRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const commitText = (key: "hero_title" | "hero_subtitle" | "hero_cta_text", val: string) => {
    if (!val.trim()) return;
    saveSettings({ [key]: val.trim() } as Partial<ShopSettings>);
  };

  const pageStyle: React.CSSProperties = {};
  if (settings?.background_style === "solid" && settings?.background_color) {
    pageStyle.background = settings.background_color;
  } else if (
    settings?.background_style === "gradient" &&
    settings?.background_gradient_enabled &&
    settings?.background_gradient_from &&
    settings?.background_gradient_to
  ) {
    pageStyle.background = `linear-gradient(135deg, ${settings.background_gradient_from}, ${settings.background_gradient_to})`;
  }

  const ribbonStyle: React.CSSProperties =
    ribbonGradFrom && ribbonGradTo
      ? { background: `linear-gradient(135deg, ${ribbonGradFrom}, ${ribbonGradTo})` }
      : ribbonColor
        ? { background: ribbonColor }
        : {};

  const previewWidthClass =
    previewMode === "mobile" && isEditor && editorOpen
      ? "mx-auto max-w-[430px] ring-1 ring-border rounded-3xl p-3 bg-background/40"
      : "";

  const sortedEls = [...heroElements].sort((a, b) => a.order - b.order);
  const heroStyle: React.CSSProperties = {
    ...(bgImage
      ? {
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {}),
    ...(heroHeight > 0 ? { minHeight: `${heroHeight}px` } : {}),
    borderRadius: fullWidth ? 0 : `${heroBorderRadius}px`,
  };

  const renderEl = (el: HeroElement) => {
    if (!el.visible && !liveEdit) return null;
    const inner = (() => {
      switch (el.id) {
        case "title":
          return (
            <h1
              className="text-3xl font-bold tracking-tight md:text-5xl outline-none focus:ring-2 focus:ring-white/40 rounded-md px-1 -mx-1"
              contentEditable={liveEdit && isEditor}
              suppressContentEditableWarning
              onBlur={(e) => commitText("hero_title", e.currentTarget.innerText)}
            >
              {displayTitle}
            </h1>
          );
        case "subtitle":
          return (
            <p
              className="mt-3 text-base md:text-lg text-primary-foreground/90 outline-none focus:ring-2 focus:ring-white/40 rounded-md px-1 -mx-1"
              contentEditable={liveEdit && isEditor}
              suppressContentEditableWarning
              onBlur={(e) => commitText("hero_subtitle", e.currentTarget.innerText)}
            >
              {displaySubtitle}
            </p>
          );
        case "cta":
          if (!ctaEnabled && !(liveEdit && isEditor)) return null;
          return (
            <Button
              size="lg"
              className={cn(
                "rounded-full bg-card px-8 text-base font-semibold text-foreground shadow-pop hover:bg-card/90",
                !ctaEnabled && "opacity-40 ring-2 ring-dashed ring-white/50",
              )}
              onClick={() =>
                !liveEdit &&
                document.getElementById("products-grid")?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <span
                contentEditable={liveEdit && isEditor}
                suppressContentEditableWarning
                onBlur={(e) => commitText("hero_cta_text", e.currentTarget.innerText)}
                className="outline-none"
              >
                {displayCta}
              </span>
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          );
        case "ai":
          if (!aiEnabled) return null;
          return (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full gap-1.5 text-primary-foreground/80 hover:bg-white/20 hover:text-white"
              onClick={() => !liveEdit && setShowAI(true)}
            >
              <Sparkles className="h-3.5 w-3.5" /> Not sure what to order?
            </Button>
          );
      }
    })();
    if (!inner) return null;
    return (
      <LiveWrap
        key={el.id}
        el={el}
        live={liveEdit && isEditor}
        hidden={!el.visible}
        onToggle={() => updateElement(el.id, { visible: !el.visible })}
        onDropOn={(fromId) => reorderElements(fromId, el.id)}
      >
        {inner}
      </LiveWrap>
    );
  };

  const heroTextEls = sortedEls.filter((e) => e.id === "title" || e.id === "subtitle");
  const heroCtaEls = sortedEls.filter((e) => e.id === "cta" || e.id === "ai");

  return (
    <div className="-mx-6 -mt-8 px-6 pt-8 transition-colors" style={pageStyle}>
      {isEditor && (
        <ShopEditor
          open={editorOpen}
          settings={settings}
          onClose={() => setEditorOpen(false)}
          previewMode={previewMode}
          onPreviewModeChange={setPreviewMode}
          liveEdit={liveEdit}
          onLiveEditChange={setLiveEdit}
        />
      )}
      {isEditor && (
        <ShopBuilder open={builderOpen} settings={settings} onClose={() => setBuilderOpen(false)} />
      )}
      {isEditor && !inlineBuilder && (
        <div className="mb-3 flex items-center justify-end gap-2">
          <button
            onClick={() => setInlineBuilder(true)}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-primary/40 transition-colors flex items-center gap-1.5"
          >
            <Sparkles className="h-3.5 w-3.5" /> Edit page (builder)
            {settings?.use_builder && (
              <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                Live
              </span>
            )}
          </button>
          <button
            onClick={() => setBuilderOpen(true)}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-primary/40 transition-colors flex items-center gap-1.5"
            title="Open full-screen builder"
          >
            Full-screen
          </button>
        </div>
      )}
      {inlineBuilder && isEditor ? (
        <InlineShopBuilder
          settings={settings}
          liveProducts={liveProducts}
          onExit={() => setInlineBuilder(false)}
        />
      ) : settings?.use_builder &&
        Array.isArray(settings.builder_rows) &&
        settings.builder_rows.length > 0 ? (
        <div className="space-y-5">
          {settings.builder_rows.map((row) => (
            <div key={row.id} className="grid grid-cols-12 gap-4">
              {row.columns.map((col) => (
                <div
                  key={col.id}
                  className="space-y-4"
                  style={{ gridColumn: `span ${col.span} / span ${col.span}` }}
                >
                  {col.widgets.map((w) => (
                    <WidgetRenderer key={w.id} widget={w} ctx={{ liveProducts }} />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Sticky compact bar — shows after hero scrolls out */}
          {stickyHeader && showStickyBar && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="fixed left-0 right-0 top-0 z-40 md:left-64"
              style={
                bgImage
                  ? {
                      backgroundImage: `url(${bgImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : {}
              }
            >
              {bgImage && <div className="absolute inset-0 bg-black/50" />}
              <div
                className="relative bg-gradient-hero text-primary-foreground"
                style={bgImage ? { background: "transparent" } : {}}
              >
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5">
                  <span className="truncate text-sm font-semibold">{displayTitle}</span>
                  {ctaEnabled && (
                    <Button
                      size="sm"
                      className="rounded-full bg-card text-foreground hover:bg-card/90 shrink-0"
                      onClick={() =>
                        document
                          .getElementById("products-grid")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      {displayCta} <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
          <div className={cn(previewWidthClass)}>
            {/* Hero — scroll-animated shrink when sticky is on */}
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                ...heroStyle,
                // Scroll-driven: nudge padding & opacity so the hero "pushes up" as user scrolls
                paddingTop: stickyHeader ? heroPaddingY : undefined,
                paddingBottom: stickyHeader ? heroPaddingY : undefined,
                opacity: stickyHeader ? heroOpacity : 1,
                scale: stickyHeader ? heroFontScale : 1,
              }}
              className={cn(
                "relative overflow-hidden bg-gradient-hero px-6 py-10 text-primary-foreground shadow-pop sm:px-10 sm:py-14 transition-[border-radius]",
                fullWidth ? "-mx-6" : "",
              )}
            >
              {bgImage && (
                <div
                  className="absolute inset-0 bg-black/40"
                  style={{ borderRadius: fullWidth ? 0 : `${heroBorderRadius}px` }}
                />
              )}
              <div
                aria-hidden
                className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[oklch(0.88_0.16_95/0.35)] blur-2xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-24 -left-12 h-64 w-64 rounded-full bg-[oklch(0.7_0.22_350/0.35)] blur-2xl"
              />
              {isEditor && (
                <div className="absolute right-3 top-3 z-10 flex flex-wrap gap-2 sm:right-5 sm:top-5">
                  {liveEdit && (
                    <span className="flex items-center gap-1 rounded-full bg-emerald-500/40 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
                      Live edit on
                    </span>
                  )}
                  <button
                    onClick={() => setEditorOpen(true)}
                    className="flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/30 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit shop
                  </button>
                </div>
              )}
              <div
                className={cn(
                  "relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between",
                  fullWidth ? "mx-auto max-w-7xl" : "",
                )}
              >
                <div className="max-w-2xl">{heroTextEls.map(renderEl)}</div>
                <div className="flex flex-col gap-2">{heroCtaEls.map(renderEl)}</div>
              </div>
              {liveEdit && isEditor && (
                <div
                  onMouseDown={onResizeStart}
                  className="absolute inset-x-0 bottom-0 z-20 flex h-3 cursor-ns-resize items-center justify-center bg-white/10 hover:bg-white/30"
                  title="Drag to resize hero"
                >
                  <div className="h-1 w-12 rounded-full bg-white/60" />
                </div>
              )}
            </motion.div>

            <div style={{ height: gapHeroFeatured }} />
            {(featuredProducts.length > 0 || (liveEdit && isEditor)) && (
              <LiveSectionWrap
                live={liveEdit && isEditor}
                label="Featured strip"
                hidden={hideFeatured}
                onToggle={() => setHideFeatured((v) => !v)}
              >
                {!hideFeatured && featuredProducts.length > 0 && (
                  <section>
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-bold">
                      <Star className="h-4 w-4 text-primary" /> Featured
                    </h2>
                    <FeaturedSection
                      products={featuredProducts}
                      layout={featuredLayout}
                      cardStyle={cardStyle}
                      ribbonStyle={ribbonStyle}
                    />
                  </section>
                )}
                {hideFeatured && liveEdit && isEditor && (
                  <div className="rounded-2xl border border-dashed border-border py-6 text-center text-xs text-muted-foreground">
                    Featured strip hidden — click eye to restore
                  </div>
                )}
              </LiveSectionWrap>
            )}

            <div style={{ height: gapFeaturedProducts }} />
            <section id="products-grid">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-2xl font-bold">All products</h2>
                {liveEdit && isEditor && productOrder.length > 1 && (
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <DragHandle className="h-3 w-3" /> Drag cards to reorder
                  </span>
                )}
              </div>
              {isLoading ? (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-64 rounded-3xl bg-muted animate-pulse" />
                  ))}
                </div>
              ) : liveProducts.length === 0 ? (
                <div className="rounded-3xl border border-border bg-card p-12 text-center shadow-soft">
                  <ShoppingBag className="mx-auto mb-4 h-10 w-10 text-muted-foreground/40" />
                  <p className="text-muted-foreground">
                    No products available yet. Check back soon.
                  </p>
                </div>
              ) : (
                <div
                  className={cn(
                    "grid gap-5",
                    cardStyle === "compact"
                      ? "sm:grid-cols-3 lg:grid-cols-4"
                      : "sm:grid-cols-2 lg:grid-cols-3",
                  )}
                >
                  {sortedLiveProducts.map((p) => (
                    <ProductCard
                      key={p.id}
                      p={p}
                      cardStyle={cardStyle}
                      ribbonStyle={ribbonStyle}
                      liveEdit={liveEdit && isEditor}
                      onDropOn={(fromId: string) => reorderProducts(fromId, p.id)}
                    />
                  ))}
                </div>
              )}
            </section>

            {showAI && aiEnabled && <FlightDeckAI onClose={() => setShowAI(false)} />}
          </div>
        </>
      )}
    </div>
  );
}

function LiveWrap({
  el,
  live,
  hidden,
  onToggle,
  onDropOn,
  children,
}: {
  el: HeroElement;
  live: boolean;
  hidden: boolean;
  onToggle: () => void;
  onDropOn: (fromId: string) => void;
  children: React.ReactNode;
}) {
  if (!live) return <>{children}</>;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", el.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const from = e.dataTransfer.getData("text/plain");
        if (from && from !== el.id) onDropOn(from);
      }}
      className={cn(
        "group/live relative rounded-xl ring-1 ring-white/20 transition-all",
        hidden ? "opacity-40" : "",
        "hover:ring-white/60 p-1",
      )}
    >
      <div className="absolute -top-3 left-2 z-20 flex items-center gap-1 rounded-full bg-foreground/90 px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 group-hover/live:opacity-100 transition-opacity">
        <span aria-hidden className="cursor-grab">
          <GripVertical className="h-3 w-3" />
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="hover:text-primary"
          aria-label={hidden ? `Show ${el.id}` : `Hide ${el.id}`}
          aria-pressed={!hidden}
        >
          {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
        <span className="capitalize">{el.id}</span>
      </div>
      {children}
    </div>
  );
}

function LiveSectionWrap({
  live,
  label,
  hidden,
  onToggle,
  children,
}: {
  live: boolean;
  label: string;
  hidden: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  if (!live) return <>{children}</>;
  return (
    <div
      className={cn(
        "group/section relative rounded-2xl ring-1 ring-border/60 p-2 transition-all",
        hidden && "opacity-50",
      )}
    >
      <div className="absolute -top-3 left-3 z-20 flex items-center gap-1 rounded-full bg-foreground/90 px-2 py-0.5 text-[10px] font-medium text-background opacity-0 group-hover/section:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={onToggle}
          className="hover:text-primary"
          aria-label={hidden ? `Show ${label}` : `Hide ${label}`}
        >
          {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
        <span>{label}</span>
      </div>
      {children}
    </div>
  );
}

function FeaturedSection({
  products,
  layout,
  cardStyle,
  ribbonStyle,
}: {
  products: RichProduct[];
  layout: "grid" | "bento" | "carousel";
  cardStyle: "compact" | "standard" | "large";
  ribbonStyle: React.CSSProperties;
}) {
  if (layout === "carousel") {
    return (
      <div className="flex gap-5 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory">
        {products.map((p) => (
          <div key={p.id} className="snap-start shrink-0 w-72">
            <ProductCard p={p} featured cardStyle={cardStyle} ribbonStyle={ribbonStyle} />
          </div>
        ))}
      </div>
    );
  }
  if (layout === "bento" && products.length > 0) {
    const [first, ...rest] = products;
    return (
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 lg:row-span-2">
          <ProductCard p={first} featured cardStyle="large" ribbonStyle={ribbonStyle} />
        </div>
        {rest.map((p) => (
          <ProductCard key={p.id} p={p} featured cardStyle={cardStyle} ribbonStyle={ribbonStyle} />
        ))}
      </div>
    );
  }
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((p) => (
        <ProductCard key={p.id} p={p} featured cardStyle={cardStyle} ribbonStyle={ribbonStyle} />
      ))}
    </div>
  );
}

function ProductCard({
  p,
  featured = false,
  cardStyle = "standard",
  ribbonStyle = {},
  liveEdit = false,
  onDropOn,
}: {
  p: RichProduct;
  featured?: boolean;
  cardStyle?: "compact" | "standard" | "large";
  ribbonStyle?: React.CSSProperties;
  liveEdit?: boolean;
  onDropOn?: (fromId: string) => void;
}) {
  const aspect =
    cardStyle === "large"
      ? "aspect-[16/10]"
      : cardStyle === "compact"
        ? "aspect-square"
        : "aspect-[4/3]";
  const padding = cardStyle === "compact" ? "p-3" : "p-5";
  return (
    <div
      draggable={liveEdit}
      onDragStart={
        liveEdit
          ? (e) => {
              e.dataTransfer.setData("text/plain", p.id);
              e.dataTransfer.effectAllowed = "move";
            }
          : undefined
      }
      onDragOver={liveEdit ? (e) => e.preventDefault() : undefined}
      onDrop={
        liveEdit
          ? (e) => {
              e.preventDefault();
              const from = e.dataTransfer.getData("text/plain");
              if (from && from !== p.id && onDropOn) onDropOn(from);
            }
          : undefined
      }
      className={cn(
        liveEdit && "ring-1 ring-border/60 rounded-3xl cursor-grab active:cursor-grabbing",
      )}
    >
      <Link
        to="/app/customer/shop/$productId"
        params={{ productId: p.id }}
        className="group relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-pop block"
        onClick={liveEdit ? (e) => e.preventDefault() : undefined}
      >
        {featured && (
          <span className="absolute left-4 top-4 z-10 flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">
            <Star className="h-3 w-3" /> Featured
          </span>
        )}
        {p.ribbon_enabled && p.ribbon_text && (
          <span
            className="absolute right-4 top-4 z-10 rounded-full bg-gradient-hero px-3 py-0.5 text-xs font-semibold text-primary-foreground shadow-soft"
            style={ribbonStyle}
          >
            {p.ribbon_text}
          </span>
        )}
        {p.image_main_url ? (
          <div className={cn(aspect, "overflow-hidden")}>
            <img
              src={p.image_main_url}
              alt={p.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        ) : (
          <div
            className={cn(
              aspect,
              "flex items-center justify-center bg-gradient-to-br from-muted/60 to-muted",
            )}
          >
            <ShoppingBag className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        <div className={padding}>
          <h3
            className={cn(
              "font-bold leading-tight",
              cardStyle === "compact" ? "text-sm" : "text-base",
            )}
          >
            {p.name}
          </h3>
          {cardStyle !== "compact" && p.short_description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{p.short_description}</p>
          )}
          <div
            className={cn(
              "flex items-center justify-between",
              cardStyle === "compact" ? "mt-2" : "mt-3",
            )}
          >
            <span className="text-sm font-semibold">
              From{" "}
              <span className="text-primary">
                £{parseFloat(String(p.price_per_m || "0")).toFixed(2)}
              </span>
              /m
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-primary transition-all group-hover:gap-2">
              View <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}
